import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Cities = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    cityid: null,
    stateid: '',
    citycode: '',
    cityname: '',
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchCountries();
    fetchStates();
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, cities]);

  const fetchCountries = async () => {
    try {
      // ✅ Use apiClient with credentials
      const response = await apiClient.get('/Countries.php', {
        params: { isactive: 1 }
      });
      
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchStates = async () => {
    try {
      // ✅ Use apiClient with credentials
      const response = await apiClient.get('/States.php', {
        params: { isactive: 1 }
      });
      
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchCities = async () => {
    setLoading(true);
    try {
      // ✅ Use apiClient with credentials
      const response = await apiClient.get('/Cities.php');
      
      if (response.data.success) {
        setCities(response.data.data || []);
      } else {
        alert('Failed to fetch cities: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching cities. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterCities = () => {
    if (!searchTerm.trim()) {
      setFilteredCities(cities);
    } else {
      const filtered = cities.filter(city =>
        city.cityname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.citycode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.statename?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.stateid) {
      newErrors.stateid = 'State is required';
    }
    
    if (!formData.citycode?.trim()) {
      newErrors.citycode = 'City code is required';
    } else if (formData.citycode.length > 10) {
      newErrors.citycode = 'City code must be 10 characters or less';
    }
    
    if (!formData.cityname.trim()) {
      newErrors.cityname = 'City name is required';
    } else if (formData.cityname.length > 100) {
      newErrors.cityname = 'City name must be 100 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // ✅ Use apiClient
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Cities.php', {
        ...formData,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'City updated successfully!' : 'City created successfully!');
        setShowModal(false);
        resetForm();
        fetchCities();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving city:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving city. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (city) => {
    setFormData({
      cityid: city.cityid,
      stateid: city.stateid,
      citycode: city.citycode,
      cityname: city.cityname,
      isactive: city.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this city?')) {
      return;
    }
    
    try {
      // ✅ Use apiClient
      const response = await apiClient.delete('/Cities.php', {
        data: { cityid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('City deleted successfully!');
        fetchCities();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting city:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting city. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      cityid: null,
      stateid: '',
      citycode: '',
      cityname: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Cities Master</h2>
          <p className="subtitle">Manage city information</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add City
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by city name, code, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredCities.length} {filteredCities.length === 1 ? 'city' : 'cities'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading cities...</div>
      ) : filteredCities.length === 0 ? (
        <div className="empty-state">
          <p>No cities found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>State</th>
                <th>City Code</th>
                <th>City Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCities.map((city) => (
                <tr key={city.cityid}>
                  <td>{city.statename || 'N/A'}</td>
                  <td className="font-mono">{city.citycode}</td>
                  <td className="font-semibold">{city.cityname}</td>
                  <td>
                    <span className={`status-badge ${city.isactive ? 'active' : 'inactive'}`}>
                      {city.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(city.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(city)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(city.cityid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit City' : 'Add New City'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="stateid">
                  State <span className="required">*</span>
                </label>
                <select
                  id="stateid"
                  value={formData.stateid}
                  onChange={(e) => setFormData({ ...formData, stateid: e.target.value })}
                  className={errors.stateid ? 'error' : ''}
                >
                  <option value="">-- Select State --</option>
                  {states.map((state) => (
                    <option key={state.stateid} value={state.stateid}>
                      {state.statename} ({state.countryname})
                    </option>
                  ))}
                </select>
                {errors.stateid && (
                  <span className="error-message">{errors.stateid}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="citycode">
                    City Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="citycode"
                    value={formData.citycode}
                    onChange={(e) => setFormData({ ...formData, citycode: e.target.value.toUpperCase() })}
                    maxLength="10"
                    placeholder="e.g., RYD, DXB"
                    className={errors.citycode ? 'error' : ''}
                  />
                  {errors.citycode && (
                    <span className="error-message">{errors.citycode}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="cityname">
                    City Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cityname"
                    value={formData.cityname}
                    onChange={(e) => setFormData({ ...formData, cityname: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Riyadh, Dubai"
                    className={errors.cityname ? 'error' : ''}
                  />
                  {errors.cityname && (
                    <span className="error-message">{errors.cityname}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cities;